import React, { useEffect, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from "../../utils/auth/useAuth";
import { LoadingSpinner, Card, Icon, Breadcrumb, Switcher, Tooltip, Search } from "pepsico-ds";
import { Row, Col } from "antd";
import { FetchLandingPageAction, FetchProcurementPageAction } from "../../redux/action/LandingPageAction";
import { getUserClaims } from "../../utils/auth/unified-auth";
import { localStoreKeys } from '../../constants/localStoreKeys';
import { setKeyValue } from "../../redux/reducer/localDataSlice";
import AcademyCard from "../../components/Cards/AcademyCard";
import CommodityPageHeader from "../../components/Header/CommodityPageHeader";
import Proc_Academy_Banner from '../../assets/Proc_Academy_Banner.svg';
import Pepsico_Course from '../../assets/Pepsico_Course.svg';
import { FetchCommodityLandingAction } from "../../redux/action/RISIAction";
import Digital_Proc_Course from '../../assets/Digital_Proc_Course.svg';
import Fastmarkets from '../../assets/Fastmarkets.svg';
import SmartCube from '../../assets/Smartcube.svg';
import CourseCard from "../../components/Cards/CourseCard";
import Advanced_Course from '../../assets/Advanced_Course.svg';
import PackagingUniversitySvg from '../../assets/packaging-university.svg';
import PH_Banner_Logo from '../../assets/PH_Banner_Logo.svg';
import SpendHQ from '../../assets/SpendHQ.svg';
import { call7DaysNpsLogic } from "../../utils/utils";
import { CAPABILITY_ICONS } from "./ProcurementAcademy";
import "./ProcurementAcademy.scss";

const NON_L1_SEARCH_ICON = (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d="M12.6667 12.6667H3.33333V3.33333H8V2H3.33333C2.59333 2 2 2.6 2 3.33333V12.6667C2 13.4 2.59333 14 3.33333 14H12.6667C13.4 14 14 13.4 14 12.6667V8H12.6667V12.6667ZM9.33333 2V3.33333H11.7267L5.17333 9.88667L6.11333 10.8267L12.6667 4.27333V6.66667H14V2H9.33333Z" fill="#2D2D2D" />
    </svg>
);

const toSearchText = (value) => String(value ?? "");

const NO_SEARCH_RESULTS_ICON = (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
        <path d="M6.66 0C2.98 0 0 2.98667 0 6.66667C0 10.3467 2.98 13.3333 6.66 13.3333C10.3467 13.3333 13.3333 10.3467 13.3333 6.66667C13.3333 2.98667 10.3467 0 6.66 0ZM6.66667 12C3.72 12 1.33333 9.61333 1.33333 6.66667C1.33333 3.72 3.72 1.33333 6.66667 1.33333C9.61333 1.33333 12 3.72 12 6.66667C12 9.61333 9.61333 12 6.66667 12ZM7 3.33333H6V7.33333L9.5 9.43333L10 8.61333L7 6.83333V3.33333Z" fill="#2D2D2D" />
    </svg>
);

const normalizeProcurementRoute = (url = "") =>
    url.replace(/^\/overview(?=\/procurement-capabilities(?:\/|$))/, "");


const formatRouteLabel = (segment = "") => segment
    .split("-")
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");


function CommonAcademy() {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { authState } = useAuth();
    const [userInfo, setUserInfo] = useState({});
    const [isDataLoading, setIsDataLoading] = useState(false);
    const [isUserDataFetched, setIsUserDataFetched] = useState(false);
    const { pageId } = useParams();
    const capability = location?.state?.capability;
    const [highlightCardId, setHighlightCardId] = useState(null);
    const [highlightCardReady, setHighlightCardReady] = useState(false);
    const [isHighlightBackdropVisible, setIsHighlightBackdropVisible] = useState(false);
    const cardRefs = useRef({});
    const appliedHighlightLocationKey = useRef(null);
    const resolvedHighlightLocationKey = useRef(null);
    const dismissedHighlightLocationKey = useRef(null);
    const learningType = location?.state?.learningType || capability?.learningType || "All Learnings";
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const [searchQuery, setSearchQuery] = useState(location?.state?.searchQuery || "");

    useEffect(() => {
        async function fetchUserInfo() {
            const user = await getUserClaims();
            if (user) {
                setUserInfo(user);
                dispatch(FetchLandingPageAction(user));
                setIsUserDataFetched(true)
            }
        }
        setIsDataLoading(true)
        fetchUserInfo();
    }, []);

    const userData = useSelector((state) => state.getLandingPageReducer);
    const cardData = useSelector((state) => state.getProcurementPageReducer);
    const [searchIndex, setSearchIndex] = useState([]);

    useEffect(() => {
        setSearchIndex(Array.isArray(cardData?.searchIndex) ? cardData.searchIndex : []);
    }, [cardData?.searchIndex]);

    useEffect(() => {
        if (cardData?.type === "SUCCESS_PROC_PAGE_ACTION") {
            setIsDataLoading(false);
        }
    }, [cardData]);
    const pageDetails = cardData?.procurementLearning?.pageDetails || {};
    const routeSegments = location.pathname
        .replace(/^\/procurement-capabilities\/?/, "")
        .split("/")
        .filter(Boolean);
    const routePageLabel = formatRouteLabel(routeSegments.length > 1 ? routeSegments[1] : routeSegments[routeSegments.length - 1]);
    const pageHeader = pageDetails.header;
    const pageSubText = pageDetails.pageDescription;
    const sectionIconPath = CAPABILITY_ICONS[pageHeader];
    const isL3Page = routeSegments.length > 1;
    const l2Route = pageDetails.l2Url || (routeSegments[0] ? `/procurement-capabilities/${routeSegments[0]}` : "/procurement-capabilities");
    const l2Label = formatRouteLabel(routeSegments[0]);
    const cardList = cardData?.courses;
    const cards = cardList?.map(card => ({
        ...card,
        courseName: card?.courseName || card?.name,
        description: card?.description || card?.desc || "",
        url: card?.courseUrl || card?.url || card?.route,
        buttonText: card?.courseUrl
            ? "Go to Course"
            : card?.route
                ? "Explore Modules"
                : card?.buttonText || "Coming Soon",
        onAction: (selectedCard) => {
            if (selectedCard?.buttonText === "Explore Modules" && selectedCard?.url) {
                setSearchQuery("");
                navigate(normalizeProcurementRoute(selectedCard.url));
                return;
            }

            // External course opens in a new tab, so the current page's search stays intact.
            if (selectedCard?.url) {
                window.open(selectedCard.url, '_blank');
            }
        },
    }));

    // Do not modify the visible cards when performing a search — only show autocomplete
    const filteredCards = useMemo(() => {
        return cards || [];
    }, [cards]);

    const searchSuggestions = useMemo(() => {
        const query = toSearchText(searchQuery).trim().toLowerCase();
        if (!query) return [];

        const source = searchIndex.length
            ? searchIndex
            : (cards || []);

        const suggestions = (source || [])
            .filter((card) => {
                if (!card) return false;
                const q = query;
                return (
                    toSearchText(card.courseName || card.name).toLowerCase().includes(q)
                );
            })
            .map((card, index) => {
                return {
                    id: card.id || card.courseId || card.route || `${card.courseName}-${index}`,
                    label: card.name || card.courseName,
                    value: card,
                    icon: card.type === "CARD" ? NON_L1_SEARCH_ICON : undefined,
                };
            });

        return suggestions.length ? suggestions : [{
            id: "no-search-results",
            label: `${toSearchText(searchQuery).trim()} - No matching content found. Try searching by Capability, Topic, or Module Title.`,
            value: { isNoSearchResults: true },
            icon: NO_SEARCH_RESULTS_ICON,
        }];
    }, [cards, searchIndex, searchQuery]);

    const handleSearchOption = (option) => {
        setIsSearchFocused(false);
        if (option.value?.isNoSearchResults) return;
        const selectedSearchQuery = option.value?.name || option.value?.courseName || option.label || "";
        setSearchQuery(selectedSearchQuery);
        if (option.value?.route && (option.value.route === '/procurement-capabilities' || option.value.route.startsWith('/procurement-capabilities/') || option.value.route.startsWith('/overview/procurement-capabilities/'))) {
            const dest = normalizeProcurementRoute(option.value.route);
            if (dest === location.pathname) {
                // Attempt to match the selected suggestion to a specific card on this page
                const needle = (option.value?.courseName || option.value?.name || option.value?.l1Category || "").toLowerCase();
                let target = dest;
                try {
                    const match = (filteredCards || []).find((card) => {
                        const name = (card.courseName || card.name || "").toLowerCase();
                        return name === needle || (needle && name.includes(needle));
                    });
                    if (match) {
                        const rawId = match.url || match.route || match.id || "";
                        target = normalizeProcurementRoute(rawId);
                    }
                } catch (e) {
                    // ignore
                }
                restartHighlight(target);
                return;
            }
            const preservedSearchQuery = selectedSearchQuery || toSearchText(searchQuery).trim();
            setSearchQuery(preservedSearchQuery);
            navigate(dest, {
                state: {
                    highlightCardId: dest,
                    highlightCardName: option.value?.name || option.value?.courseName,
                    searchQuery: preservedSearchQuery,
                },
            });
            return;
        }
        if (option.value?.type === "CARD" && option.value?.route) {
            const dest = normalizeProcurementRoute(option.value.route);
            if (dest === location.pathname) {
                restartHighlight(dest);
                return;
            }
            const preservedSearchQuery = selectedSearchQuery || toSearchText(searchQuery).trim();
            setSearchQuery(preservedSearchQuery);
            navigate(dest, {
                state: {
                    highlightCardId: dest,
                    searchQuery: preservedSearchQuery,
                },
            });
            return;
        }
        if (option.value?.url) {
            setSearchQuery("");
            window.open(option.value.url, '_blank');
        }
    };

    useEffect(() => {
        if (location?.state?.highlightCardId && appliedHighlightLocationKey.current !== location.key) {
            appliedHighlightLocationKey.current = location.key;
            setHighlightCardId(location.state.highlightCardId);
        }
    }, [location?.key, location?.state?.highlightCardId]);

    useEffect(() => {
        const pendingHighlightId = location?.state?.highlightCardId;
        if (!pendingHighlightId || !filteredCards?.length) return;
        if (resolvedHighlightLocationKey.current === location.key) return;
        // Redux still holds the previous page's cards until this page's fetch resolves;
        // matching against them can lock in a false-positive highlight target.
        if (isDataLoading || cardData?.type !== "SUCCESS_PROC_PAGE_ACTION") return;
        const pendingHighlightName = location?.state?.highlightCardName?.trim().toLowerCase();
        // Prefer an exact name match; only fall back to a substring match when nothing matches
        // exactly, since near-duplicate names (e.g. "X" vs "X in PepsiCo") both contain each other.
        const matchingCard = pendingHighlightName
            ? filteredCards.find((card) => (card.courseName || card.name || "").trim().toLowerCase() === pendingHighlightName)
                || filteredCards.find((card) => {
                    const cardName = (card.courseName || card.name || "").trim().toLowerCase();
                    return cardName.includes(pendingHighlightName) || pendingHighlightName.includes(cardName);
                })
            : null;
        const targetHighlightId = matchingCard
            ? normalizeProcurementRoute(matchingCard.url || matchingCard.route || matchingCard.id || "")
            : pendingHighlightId;
        const hasMatchingCard = filteredCards.some((card, index) => {
            const rawId = card.url || card.route || card.id || index;
            return normalizeProcurementRoute(rawId) === targetHighlightId;
        });

        if (hasMatchingCard) {
            resolvedHighlightLocationKey.current = location.key;
            setHighlightCardId(targetHighlightId);
        }
    }, [filteredCards, isDataLoading, cardData?.type, location?.state?.highlightCardId, location?.state?.highlightCardName]);

    useEffect(() => {
        if (!highlightCardId) {
            setIsHighlightBackdropVisible(false);
            return;
        }
        const el = cardRefs.current[highlightCardId];
        if (el && typeof el.scrollIntoView === 'function') {
            setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
        }
    }, [highlightCardId, location.pathname, location.state, navigate]);

    const restartHighlight = (targetId) => {
        setHighlightCardReady(false);
        setIsHighlightBackdropVisible(false);
        setHighlightCardId(targetId);
        requestAnimationFrame(() => {
            setHighlightCardReady(true);
        });
    };

    useEffect(() => {
        return () => {
            dispatch({
                type: 'RESET_PROC_PAGE_ACTION',
                payload: "RESET_PROC_PAGE_ACTION"
            });
        };
    }, []);

    /* istanbul ignore next */
    useEffect(() => {
        if (isUserDataFetched && userData && userData.type === "SUCCESS_LANDING_PAGE_ACTION") {


            // dispatch(setKeyValue({ key: localStoreKeys.isNPSSubmittedRecently, value: false }))
            // // 7 days NPS logic
            // const now = Date.now(); // current timestamp in milliseconds

            // const responseDate = new Date(userData?.mostRecentDate).getTime(); // convert to timestamp in ms

            // const diffInMs = Math.abs(now - responseDate); // difference in milliseconds

            // const daysDiff = diffInMs / (1000 * 60 * 60 * 24); // convert ms to days

            // if (daysDiff >= 7) {
            //     dispatch(setKeyValue({ key: localStoreKeys.isNPSSubmittedRecently, value: false }))
            // } else {
            //     dispatch(setKeyValue({ key: localStoreKeys.isNPSSubmittedRecently, value: true }))
            // }

            call7DaysNpsLogic(dispatch, userData)
            setIsDataLoading(true)

            const currentRoute = location.pathname.startsWith('/procurement-capabilities/')
                ? location.pathname
                : capability?.route || capability?.url || pageId;
            dispatch(FetchProcurementPageAction(currentRoute));
        }

    }, [dispatch, isUserDataFetched, userData, location.pathname, capability?.route, capability?.url, pageId])


    /* istanbul ignore next */
    if (authState?.isAuthenticated) {
        return (

            <>
                <CommodityPageHeader
                    header={"Procurement Capabilities"}
                    subtext={"Continue building your skills one step at a time with focused courses to support your growth in procurement."}
                    image={Proc_Academy_Banner}
                />

                {
                    (isDataLoading || cardData?.type !== "SUCCESS_PROC_PAGE_ACTION") ?
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: 300 }}>
                            <LoadingSpinner size={{ height: '80px', width: '80px' }} />
                        </div>
                        :
                        <>
                            <Breadcrumb
                                className="ph-breadcrumb"
                                style={{ padding: '24px 24px 24px 0px', width: '100%' }}
                                homeIcon={{
                                    icon: "school",
                                    isShowIcon: true,
                                    label: "Go to Procurement Learning",
                                    onClick: () => {
                                        setSearchQuery("");
                                        navigate("/procurement-capabilities", { state: { learningType } });
                                    }
                                }}
                                breadcrumbs={[
                                    {
                                        label: learningType,
                                        onClick: () => {
                                            setSearchQuery("");
                                            navigate("/procurement-capabilities", { state: { learningType } });
                                        }
                                    },
                                    ...(routeSegments.length > 1 ? [{
                                        label: l2Label,
                                        onClick: () => {
                                            setSearchQuery("");
                                            navigate(l2Route);
                                        }
                                    }] : []),
                                    ...(routeSegments.length > 0 ? [{ label: pageHeader }] : [])
                                ]}
                                useAnchor={false}
                            />
                            <div className="procurement-academy-slot">
                                {highlightCardId && isHighlightBackdropVisible && (
                                    <div
                                        className="pa-highlight-backdrop"
                                        aria-hidden="true"
                                    />
                                )}
                                <section className="pa-capabilities-section">
                                    <div className="pa-detail-layout">
                                        <div className="pa-detail-copy">
                                            <div className="pa-section-title-group">
                                                {!isL3Page && sectionIconPath && (
                                                    <svg className="pa-section-leading-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                                        <path d={sectionIconPath} fill="#2D2D2D" />
                                                    </svg>
                                                )}
                                                <h2 className="pa-all-learnings-title">{pageHeader}</h2>
                                            </div>
                                            <p className="pa-card-description pa-detail-description">{pageSubText}</p>
                                        </div>
                                        {console.log(searchSuggestions)}
                                        <div className="pa-detail-search">
                                            <Search
                                                className="pa-search-control"
                                                // delay={300}
                                                isAutoComplete
                                                isClearable
                                                focus={isSearchFocused}
                                                menuPosition="bottom-right"
                                                onClear={() => {
                                                    setSearchQuery("");
                                                    setIsSearchFocused(false);
                                                    setHighlightCardId(null);
                                                    setHighlightCardReady(false);
                                                    setIsHighlightBackdropVisible(false);
                                                    navigate(location.pathname, { replace: true, state: null });
                                                }}
                                                onSearch={() => setIsSearchFocused(true)}
                                                onSelectOption={handleSearchOption}
                                                onUpdate={(value) => {
                                                    const nextValue = toSearchText(value);
                                                    setSearchQuery(nextValue);
                                                    setIsSearchFocused(true);
                                                    if (!nextValue.trim() && highlightCardId) {
                                                        // Dismiss (not resolve) so the true-match effect can still run later.
                                                        dismissedHighlightLocationKey.current = location.key;
                                                        setHighlightCardId(null);
                                                        setHighlightCardReady(false);
                                                        setIsHighlightBackdropVisible(false);
                                                    }
                                                }}
                                                placeholderText="Search..."
                                                searchTerm={searchQuery}
                                                size="medium"
                                                suggestions={searchSuggestions}
                                                limitSuggestions={searchSuggestions.length}
                                                type='global'
                                            />
                                        </div>
                                    </div>

                                    <div className="pa-cards-grid">
                                        {filteredCards.map((item, index) => {
                                            const rawId = item.url || item.route || item.id || index;
                                            const itemId = normalizeProcurementRoute(rawId);
                                            const pendingHighlightName = location?.state?.highlightCardName?.trim().toLowerCase();
                                            const cardName = (item.courseName || item.name || "").trim().toLowerCase();
                                            // Only fall back to a substring match when no card matches exactly, since
                                            // near-duplicate names (e.g. "X" vs "X in PepsiCo") both contain each other.
                                            const hasExactPendingMatch = Boolean(pendingHighlightName) && filteredCards.some((candidate) => (candidate.courseName || candidate.name || "").trim().toLowerCase() === pendingHighlightName);
                                            const matchesPendingHighlight = Boolean(
                                                pendingHighlightName
                                                && cardName
                                                && dismissedHighlightLocationKey.current !== location.key
                                                && (cardName === pendingHighlightName
                                                    || (!hasExactPendingMatch && (cardName.includes(pendingHighlightName) || pendingHighlightName.includes(cardName))))
                                            );
                                            const hasResolvedHighlightCard = filteredCards.some((candidate, candidateIndex) => {
                                                const candidateId = normalizeProcurementRoute(candidate.url || candidate.route || candidate.id || candidateIndex);
                                                return candidateId === highlightCardId;
                                            });
                                            const isHighlighted = itemId === highlightCardId
                                                || (!hasResolvedHighlightCard && matchesPendingHighlight);
                                            return (
                                                <div
                                                    key={itemId}
                                                    ref={(el) => {
                                                        cardRefs.current[itemId] = el;
                                                        if (el && isHighlighted) {
                                                            setHighlightCardReady(true);
                                                        }
                                                    }}
                                                    onAnimationStart={(event) => {
                                                        if (event.animationName === 'floating-card-pulse') {
                                                            setIsHighlightBackdropVisible(true);
                                                        }
                                                    }}
                                                    onAnimationEnd={(event) => {
                                                        if (event.animationName === 'floating-card-pulse') {
                                                            setIsHighlightBackdropVisible(false);
                                                        }
                                                    }}
                                                    className={`pa-capability-card ${isHighlighted ? 'pa-card-floating-highlight' : ''}`}>
                                                    { }
                                                    <div className="pa-card-header">
                                                        <div className="pa-card-title" title={item.courseName}>
                                                            {item.courseName}
                                                        </div>
                                                    </div>

                                                    <div className="pa-card-body">
                                                        {item.description && (
                                                            <p className="pa-card-description">{item.description}</p>
                                                        )}
                                                        {(!item.courseUrl || item.duration) && (
                                                            <div className="pa-card-tag-anchor">
                                                                <span className={`pa-tag ${item.courseUrl ? "pa-course-duration-tag" : ""}`}>
                                                                    {item.courseUrl && (
                                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                                            <circle cx="12" cy="12" r="9" />
                                                                            <polyline points="12 7 12 12 15 14" />
                                                                        </svg>
                                                                    )}
                                                                    <span className="pa-tag-value">
                                                                        {item.courseUrl
                                                                            ? item.duration
                                                                            : (Number(item?.topicCount || 0) > 0
                                                                                ? `${item.topicCount} ${Number(item?.topicCount || 0) === 1 ? 'topic' : 'topics'}`
                                                                                : `${item.moduleCount || 0} ${Number(item?.moduleCount || 0) === 1 ? 'module' : 'modules'}`)}
                                                                    </span>
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="pa-card-button-row">
                                                        <button
                                                            type="button"
                                                            className={`pa-explore-button ${item.courseUrl ? "pa-course-button" : ""}`}
                                                            onClick={() => item.onAction?.(item)}
                                                            disabled={!item.url}
                                                        >
                                                            <span className="pa-explore-button-text">{item.courseUrl ? "Go to course" : item.buttonText}</span>
                                                            <span className="pa-explore-button-icon">
                                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                                    {item.courseUrl ? (
                                                                        <>
                                                                            <path d="M14 3h7v7" />
                                                                            <path d="M10 14L21 3" />
                                                                            <path d="M21 14v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h6" />
                                                                        </>
                                                                    ) : (
                                                                        <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" fill="currentColor" stroke="none" />
                                                                    )}
                                                                </svg>
                                                            </span>
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </section>
                            </div>
                        </>
                }
            </>
        );
    }
    return null;
}

export default CommonAcademy;